import Box from '@mui/material/Box';
import { alpha, type Theme } from '@mui/material/styles';
import { memo } from 'react';

import { partnerTokens, surfaceTokens } from '@/theme/themePrimitives';
import { auroraGlowFromHex } from '@/utils/color';

interface Props {
  /** Glow hex of the top blob. Defaults to the Partners Soft Blue (Signal PD2). */
  color?: string;
}

/**
 * Signal "aurora esfumaçada" over the tonal floor: three blurred blobs pinned to the viewport, so
 * the smoke stays put while the content scrolls over it. The top blob carries `color` (the
 * Partners Soft Blue by default). Hidden in dark mode.
 */
function AuroraBackdrop({ color = partnerTokens.softBlue }: Props) {
  return (
    <Box aria-hidden data-testid="signal-aurora" sx={sx.root}>
      <Box component="span" sx={sx.light} />
      <Box component="span" sx={{ ...sx.org, bgcolor: auroraGlowFromHex(color) }} />
      <Box component="span" sx={sx.blue} />
    </Box>
  );
}

export default memo(AuroraBackdrop);

const blob = {
  position: 'absolute' as const,
  display: 'block',
  borderRadius: '50%',
};

const sx = {
  root: (theme: Theme) => ({
    position: 'fixed' as const,
    inset: 0,
    overflow: 'hidden',
    pointerEvents: 'none' as const,
    zIndex: -1,
    // Own compositor layer: the blurred blobs are painted once, not on every scroll frame.
    willChange: 'transform',
    ...theme.applyStyles('dark', { display: 'none' }),
  }),
  light: {
    ...blob,
    width: 700,
    height: 520,
    left: -160,
    top: -260,
    bgcolor: (theme: Theme) => alpha(theme.palette.common.white, 0.55),
    filter: 'blur(70px)',
  },
  org: {
    ...blob,
    width: 640,
    height: 520,
    left: '34%',
    top: -280,
    filter: 'blur(100px)',
  },
  blue: {
    ...blob,
    width: 680,
    height: 680,
    right: -220,
    bottom: -320,
    bgcolor: alpha(surfaceTokens.softBlue, 0.3),
    filter: 'blur(110px)',
  },
};
